import { userDocumentsService } from '@/api/user-documents.service';
import {
  addItemToQueriesData,
  removeItemFromQueriesData,
  updateItemOnQueriesData,
} from '@/utils/update-query-data';
import type {
  CreateUserDocumentDto,
  UpdateUserDocumentDto,
  UserDocumentDto,
  UsersControllerFindAllUserDocumentsData,
} from '@carvia/ros-client-types';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/vue-query';
import type { MutationDefaultsContext } from './query-client';

const QUERY_KEY = 'user-documents';

export const useUserDocument = (userId: string, documentId: string, enabled?: boolean) => {
  return useQuery({
    queryKey: [QUERY_KEY, userId, documentId],
    queryFn: () => userDocumentsService.getOne(userId, documentId),
    enabled,
  });
};

export const useUserDocuments = (
  userId: string,
  query?: UsersControllerFindAllUserDocumentsData['query'],
) => {
  return useInfiniteQuery({
    queryKey: [QUERY_KEY],
    queryFn: ({ pageParam = 1 }) =>
      userDocumentsService.getAllPaginated(userId, { ...query, page: pageParam }),
    getNextPageParam: (lastPage) => lastPage.meta.nextPage,
    initialPageParam: 1,
  });
};

export const useUpdateUserDocument = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: [QUERY_KEY],
    mutationFn: ({
      userId,
      documentId,
      document,
    }: {
      userId: string;
      documentId: string;
      document: UpdateUserDocumentDto;
    }) => userDocumentsService.update(userId, documentId, document),
    onMutate: () => ({
      successMessage: 'User Document updated',
      invalidateQueryKeys: null,
    }),
    onSuccess: (result, variables, onMutateResult, context) => {
      queryClient.getMutationDefaults([])?.onSuccess?.(null, null, onMutateResult, context);
      updateItemOnQueriesData<UserDocumentDto>([QUERY_KEY], result, queryClient);
    },
  });
};

export const useRemoveUserDocument = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: [QUERY_KEY],
    mutationFn: ({ userId, documentId }: { userId: string; documentId: string }) =>
      userDocumentsService.remove(userId, documentId),
    onMutate: (ids) => ({ ...ids, successMessage: 'User Document deleted' }),
    onSuccess: (result, variables, onMutateResult, context) => {
      queryClient.getMutationDefaults([])?.onSuccess?.(result, variables, onMutateResult, context);
      removeItemFromQueriesData<UserDocumentDto>([QUERY_KEY], variables?.documentId, queryClient);
    },
  });
};

export const useCreateUserDocument = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: [QUERY_KEY],
    mutationFn: ({ userId, document }: { userId: string; document: CreateUserDocumentDto }) =>
      userDocumentsService.create(userId, document),
    onMutate: (): MutationDefaultsContext => ({
      successMessage: 'User Document created',
      invalidateQueryKeys: null,
    }),
    onSuccess: (result, variables, onMutateResult, context) => {
      queryClient.getMutationDefaults([])?.onSuccess?.(null, null, onMutateResult, context);
      addItemToQueriesData<UserDocumentDto>([QUERY_KEY], result, queryClient);
    },
  });
};
